import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close as CloseIcon, DeleteForeverOutlined as DeleteIcon } from '@mui/icons-material';
import { comptoirAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { ComptoirOffre, ComptoirSuppressionImpact } from '../../../types/comptoir';
import { dh, fDate, num } from '../ui';

const RED = '#D32F2F';

/**
 * Deleting an offre that has payments or livraisons (Docs/COMPTOIR_SUPPRESSION_PLAN.md):
 * anyone sends a request with a motif, a Manager approves (cancels everything, refunds the
 * cash through a caisse sortie dated today) or refuses. Both sides see the same impact.
 */
const SuppressionDialog: React.FC<{
  offre: ComptoirOffre | null;
  impact: ComptoirSuppressionImpact | null;
  onClose: () => void;
  onDone: (message: string) => void;
}> = ({ offre, impact, onClose, onDone }) => {
  const { user } = useAuth();
  const [motif, setMotif] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMotif(''); setErr(''); }, [offre?.id]);
  if (!offre || !impact) return null;

  const demande = impact.demande;
  const manager = impact.peut_valider;
  const blocked = impact.production.length > 0;
  const mine = !!demande && demande.par_id === user?.id;

  const act = async (fn: () => Promise<string>, needsMotif = true) => {
    if (needsMotif && !motif.trim()) { setErr('Indiquez le motif.'); return; }
    setBusy(true); setErr('');
    try { onDone(await fn()); } catch (e: any) { setErr(extractErrorMessage(e)); } finally { setBusy(false); }
  };
  const valider = () => act(async () => {
    const r = await comptoirAPI.validerSuppression(offre.id, motif.trim());
    const extra = [
      r.remboursement ? `remboursement caisse ${r.remboursement} : ${dh(r.remboursement_montant)}` : '',
      num(r.hors_caisse) > 0 ? `${dh(r.hors_caisse)} à rembourser hors caisse` : '',
    ].filter(Boolean).join(' · ');
    return `Offre ${offre.reference} supprimée${extra ? ` — ${extra}` : ''}`;
  });
  const refuser = () => act(async () => { await comptoirAPI.refuserSuppression(offre.id, motif.trim()); return 'Demande de suppression refusée'; });
  const demander = () => act(async () => { await comptoirAPI.demanderSuppression(offre.id, motif.trim()); return 'Demande envoyée aux Managers'; });
  const retirer = () => act(async () => { await comptoirAPI.retirerDemandeSuppression(offre.id); return 'Demande de suppression retirée'; }, false);

  const sub = manager ? 'Validation Manager' : demande ? 'Demande en attente' : 'La suppression doit être validée par un Manager';
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="suppression-title">
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha(RED, 0.12), color: RED, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><DeleteIcon /></Box>
        <Box sx={{ flex: 1 }}>
          <Typography id="suppression-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Supprimer l'offre {offre.reference}</Typography>
          <Typography variant="body2" color="text.secondary">{offre.client_nom} · {dh(offre.total)} · {sub}</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={busy} aria-label="Fermer"><CloseIcon /></IconButton>
      </Box>
      <DialogContent sx={{ pt: 2.5 }}>
        {demande && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Demandée par <b>{demande.par || '?'}</b> le {new Date(demande.le).toLocaleString('fr-FR')}
            {demande.motif && <> — « {demande.motif} »</>}
          </Alert>
        )}
        {blocked ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {impact.service_reference} est déjà utilisé en production ({impact.production.join(', ')}) : retirez-le du planning et du suivi avant de supprimer l'offre.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>La suppression annulera :</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.25 } }}>
              {impact.paiements.map((p) => (
                <li key={p.reference}>
                  Paiement <b>{p.reference}</b> du {fDate(p.date)} · {p.mode} · {dh(p.montant)}
                  {p.partage && <> (sa part sur un bon de {dh(p.montant_bon)} qui paie aussi d'autres offres)</>}
                </li>
              ))}
              {impact.livraisons.map((l) => <li key={l.reference}>Bon de livraison <b>{l.reference}</b> du {fDate(l.date)}</li>)}
              {impact.production_reference && <li><b>{impact.production_reference}</b> sera retiré de Gestion Projets / Services</li>}
            </Box>
            {num(impact.remboursement_especes) > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Caisse : une sortie « Remboursement client » de <b>{dh(impact.remboursement_especes)}</b> datée d'aujourd'hui (les jours passés ne changent pas).
              </Typography>
            )}
            {num(impact.hors_caisse) > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Hors caisse (chèque / virement) : <b>{dh(impact.hors_caisse)}</b> à rembourser au client.
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>L'offre ne pourra pas être restaurée.</Typography>
          </Alert>
        )}
        {!blocked && (manager || !demande) && (
          <TextField
            label={manager ? 'Motif de la décision' : 'Motif de la demande'} required fullWidth multiline minRows={2} autoFocus
            value={motif} onChange={(e) => setMotif(e.target.value)} disabled={busy}
            placeholder={manager ? 'ex : client remboursé en espèces' : 'ex : le client a annulé sa commande'}
          />
        )}
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {busy && <CircularProgress size={20} sx={{ mr: 1 }} />}
        <Button onClick={onClose} disabled={busy}>Fermer</Button>
        {mine && !manager && (
          <Button onClick={retirer} disabled={busy}>Retirer la demande</Button>
        )}
        {manager && demande && (
          <Button onClick={refuser} disabled={busy} variant="outlined">Refuser la demande</Button>
        )}
        {manager && !blocked && (
          <Button onClick={valider} disabled={busy} variant="contained" color="error">Supprimer et annuler</Button>
        )}
        {!manager && !demande && !blocked && (
          <Button onClick={demander} disabled={busy} variant="contained" color="error">Envoyer la demande</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SuppressionDialog;
